import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { getFenceGroupDetails, getBrickCounts, getBrickColor } from 'state/fence/fenceSelector';
import { colorNames } from 'helpers/globals';
import FormLabel from 'components/common/FormLabel/FormLabel';
import FormInput from 'components/common/FormInput/FormInput';
import FormFieldInfo from 'components/common/FormFieldInfo/FormFieldInfo';
import ButtonBase from 'components/common/ButtonBase/ButtonBase';
import ModalFullView from 'components/layout/AppModals/ModalFullView';
import AlertBase from 'components/common/AlertBase/AlertBase';
import LoaderIcon from 'components/common/LoaderIcon/LoaderIcon';
import { isEmpty, isEmail } from 'helpers/validation';
import style from './FenceCalculation.module.scss';
import FormCheck from 'components/common/FormCheck/FormCheck';

const INIT_FORM_DATA = {
  name: '',
  email: '',
  phone: '',
  city: '',
  message: '',
  type: 'offer',
};

const FenceCalculation = () => {
  const fence = useSelector((state) => state.fence);
  const fenceDetails = useSelector(getFenceGroupDetails);
  const brickCounts = useSelector(getBrickCounts(fenceDetails));
  const brickColor = useSelector(getBrickColor);
  const [errors, setErrors] = useState({});
  const [alert, setAlert] = useState({});
  const [sentTypes, setSentTypes] = useState([]);
  const [formData, setFormData] = useState({ ...INIT_FORM_DATA });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [image, setImage] = useState('');
  const [loader, setLoader] = useState(false);

  const changeInputHandler = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const changeSubmitType = (type) => {
    setFormData({ ...formData, type });
  };

  const onCreateImageHandler = (base64Image) => {
    setImage(base64Image);
  };

  const submitHandler = (e) => {
    e.preventDefault();

    const validationErrors = {};

    if (isEmpty(formData.name)) {
      validationErrors.name = 'Kötelező kitölteni.';
    }

    if (isEmpty(formData.email)) {
      validationErrors.email = 'Kötelező kitölteni.';
    } else if (!isEmail(formData.email)) {
      validationErrors.email = 'Helyes e-mail címet kell megadni.';
    }

    if (isEmpty(formData.phone)) {
      validationErrors.phone = 'Kötelező kitölteni.';
    }

    if (isEmpty(formData.city)) {
      validationErrors.city = 'Kötelező kitölteni.';
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    } else {
      setErrors({});
    }

    if (!sentTypes.includes(formData.type)) {
      setSentTypes([...sentTypes, formData.type]);
      setLoader(true);
      setAlert({});
    }

    axios({
      method: 'post',
      url: process.env.REACT_APP_API_URL,
      headers: { 'content-type': 'application/json' },
      data: {
        ...formData,
        brickCounts,
        brickColor: colorNames[fence.settings.color],
        image,
      },
    })
      .then((result) => {
        if (result.data.type === 'success') {
          setErrors({});
          setAlert(result.data);
        } else {
          setAlert(result.data);
        }
        setLoader(false);
      })
      .catch(() => {
        setAlert({
          type: 'error',
          message: 'Hiba történt, sajnos nem sikerült a küldés!',
        });

        const typeIndex = sentTypes.indexOf(formData.type);

        setSentTypes(sentTypes.splice(typeIndex, 1));
        setLoader(false);
      });
  };

  useEffect(() => {
    if (sentTypes.length === 2) {
      setFormData({ ...INIT_FORM_DATA });
    }
  }, [sentTypes.length]);

  return (
    <div className={style.fenceCalculation}>
      <h2>Ajánlatkérés</h2>

      <div className="row">
        <div className="col-md-5">
          <p>
            A kerítéstervező program az ön által megadott adatok alapján számítja ki a hozzávetőleges darabszám
            mennyiséget:
          </p>

          <ul className={style.fenceCalculation_elements}>
            <li>
              <img src={brickColor.normal} alt="" />
              <div>
                Normálkő kalkuláció
                <br />
                <strong>{brickCounts.normal} db</strong>
              </div>
            </li>
            <li>
              <img src={brickColor.half} alt="" />
              <div>
                Félkő kalkuláció
                <br />
                <strong>{brickCounts.half} db</strong>
              </div>
            </li>
            <li>
              <img src={brickColor.topCover} alt="" />
              <div>
                Fedőlap kalkuláció
                <br />
                <strong>{brickCounts.topCover} db</strong>
              </div>
            </li>
          </ul>
        </div>

        <form className="col-md-6 offset-md-1" onSubmit={submitHandler}>
          <div className="mb-sm">
            <FormLabel htmlFor="calcName" className="text-lg mb-xs">
              * Név:
            </FormLabel>
            <FormInput value={formData.name} id="calcName" name="name" onChange={changeInputHandler} />
            {errors.name && <FormFieldInfo>{errors.name}</FormFieldInfo>}
          </div>

          <div className="mb-sm">
            <FormLabel htmlFor="calcEmail" className="text-lg mb-xs">
              * E-mail cím:
            </FormLabel>
            <FormInput type="email" value={formData.email} id="calcEmail" name="email" onChange={changeInputHandler} />
            {errors.email && <FormFieldInfo>{errors.email}</FormFieldInfo>}
          </div>

          <div className="mb-sm">
            <FormLabel htmlFor="calcPhone" className="text-lg mb-xs">
              * Telefonszám:
            </FormLabel>
            <FormInput value={formData.phone} id="calcPhone" name="phone" onChange={changeInputHandler} />
            {errors.phone && <FormFieldInfo>{errors.phone}</FormFieldInfo>}
          </div>

          <div className="mb-sm">
            <FormLabel htmlFor="calcCity" className="text-lg mb-xs">
              * Város/település <span className="text-sm">(szállítási költségek számításához)</span>:
            </FormLabel>
            <FormInput value={formData.city} id="calcCity" name="city" onChange={changeInputHandler} />
            {errors.city && <FormFieldInfo>{errors.city}</FormFieldInfo>}
          </div>

          <div className="mb-md">
            <FormLabel htmlFor="calcMessage" className="text-lg mb-xs">
              Megjegyzés:
            </FormLabel>
            <FormInput
              rows="4"
              value={formData.message}
              id="calcMessage"
              name="message"
              onChange={changeInputHandler}
            />
          </div>

          <div className="mb-md">
            <FormCheck
              mode="normal"
              type="checkbox"
              checked={privacyAccepted}
              onChange={() => setPrivacyAccepted(!privacyAccepted)}
            >
              Hozzájárulok ahhoz, hogy a Frühwald munkatársa telefonon megkeressen kötelezettség nélküli ajánlattal
            </FormCheck>
          </div>

          {loader && <LoaderIcon className="ms-auto me-auto mb-md" />}

          {Object.keys(alert).length ? <AlertBase type={alert.type}>{alert.message}</AlertBase> : ''}

          <div className="row">
            <div className="col-md-6 mb-md">
              <ButtonBase
                type="submit"
                color="primary"
                className="text-transform-none w-full"
                onClick={() => changeSubmitType('offer')}
                disabled={sentTypes.includes('offer') || !privacyAccepted}
              >
                Elküldöm ajánlatkérésre
              </ButtonBase>
            </div>

            <div className="col-md-6 mb-md">
              <ButtonBase
                type="submit"
                color="primary"
                className="text-transform-none w-full"
                onClick={() => changeSubmitType('myself')}
                disabled={sentTypes.includes('myself') || !privacyAccepted}
              >
                Elküldöm magamnak e-mailben
              </ButtonBase>
            </div>
          </div>
        </form>
      </div>

      <ModalFullView createImage className={style.modalFullView} onCreateImage={onCreateImageHandler} />
    </div>
  );
};

export default FenceCalculation;
